import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
  type LoaderFunctionArgs,
} from "react-router";
import { BRAND } from "./brand";
import { shouldLoadAppBridge } from "./lib/embedded";
import { env } from "./env.server";
import { resolveLocaleFromRequest } from "./i18n/locale.server";
import { I18nProvider } from "./i18n/context";

// Inline SVG favicon as data URI — L in a rounded square. Avoids the 404 noise
// without requiring a public/ asset pipeline.
const FAVICON_DATA_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='12' fill='%231f2937'/%3E%3Ctext x='32' y='44' font-family='system-ui,-apple-system,Helvetica,sans-serif' font-size='38' font-weight='700' text-anchor='middle' fill='%23ffffff'%3EL%3C/text%3E%3C/svg%3E";

export const links = () => [
  { rel: "icon", href: FAVICON_DATA_URI, type: "image/svg+xml" },
];

// Default document <title> for the whole app, rendered by <Meta /> in the head. Child routes
// override it via their own meta(). We do NOT also hardcode a literal <title> in the Layout —
// that produced two <title> tags and a generic title that overrode the route's.
export const meta = () => [{ title: BRAND.name }];

// Root loader supplies the Shopify API key so the api-key meta + App Bridge
// script can BOTH live in <head>. Per Shopify docs, App Bridge looks for
// shopify-api-key in <head> before the script runs — if the meta is in body
// (injected by a child route layout), App Bridge silently fails to register
// Polaris Web Components, leaving <s-*> tags as unstyled inline text.
//
// `isEmbedded` MUST stay true for every request in an /app/* session (documents
// AND React Router ".data" single-fetch navigations) — see shouldLoadAppBridge.
// If it flips false mid-session, React tears the App Bridge <script> out of
// <head>, killing the session-token fetch patch, and the next loader bounces to
// /auth/login inside the iframe (review blocker 2.1.1, ref 116508).
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const isEmbedded = shouldLoadAppBridge(url.pathname);
  const locale = resolveLocaleFromRequest(request);
  return { apiKey: env().SHOPIFY_API_KEY, isEmbedded, locale };
};

type RootLoaderData = Awaited<ReturnType<typeof loader>>;

// Layout owns the single <html>+<head>+<body> shell for BOTH the successful
// render (App below) AND the ErrorBoundary render. Previously App() and
// ErrorBoundary() each returned their OWN <html> tree, so on any error page
// (e.g. a 404) the client hydration walk diverged from the SSR output and
// React spewed #418/#423 hydration-mismatch console errors — which Shopify
// reviewers see. Defining the document exactly once eliminates that.
// RR7 documented pattern; see
// https://reactrouter.com/api/framework-conventions/root.tsx
//
// Because Layout renders in both success and error flows, the root loader data
// is read via useRouteLoaderData("root") (NOT useLoaderData) — it returns
// undefined while the ErrorBoundary is rendering, which is exactly what we
// want: error pages (404s, thrown loaders) are never embedded, so the App
// Bridge block must NOT mount there.
export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData("root") as RootLoaderData | undefined;
  return (
    <html lang={data?.locale ?? "en"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        {data?.isEmbedded ? (
          <>
            {/* App Bridge = ui-* components + APIs (modal, toast, nav, save bar).
                Polaris  = s-*  components (s-page, s-card, s-button, s-banner).
                Both scripts must be in <head> AFTER the api-key meta, no defer/async. */}
            <meta name="shopify-api-key" content={data.apiKey} />
            <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js" />
            <script src="https://cdn.shopify.com/shopifycloud/polaris.js" />
          </>
        ) : null}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const data = useRouteLoaderData("root") as RootLoaderData | undefined;
  return (
    <I18nProvider initialLocale={data?.locale ?? "en"}>
      <Outlet />
    </I18nProvider>
  );
}

// Catches anything an inner route doesn't handle: response-shaped errors
// (throw new Response("Not found", { status: 404 })), thrown loaders/actions,
// or render-time exceptions. Production-safe: never leaks stack traces; logs
// to console. Returns ONLY the inner content — Layout above supplies
// <html>/<head>/<body>/<Scripts>.
export function ErrorBoundary() {
  const error = useRouteError();
  const isResponse = isRouteErrorResponse(error);
  const status = isResponse ? error.status : 500;
  const isProd = typeof process !== "undefined" && process.env.NODE_ENV === "production";

  // Only log genuine server-side failures. Expected 4xx responses — chiefly the
  // constant stream of bot/scanner 404 probes to /meta.json, /wp-login.php,
  // /.env — are noise that buries real errors in Railway log greps (server-side
  // reporting is also filtered in entry.server's handleError). status is 500
  // for non-route (thrown) errors, so those are still logged.
  if (typeof console !== "undefined" && status >= 500) {
    console.error("[root-error-boundary]", status, isResponse ? error.statusText : error);
  }

  const headline =
    status === 404
      ? "We couldn't find that page"
      : status === 401 || status === 403
        ? "You're not signed in"
        : status >= 500
          ? "Something went wrong"
          : "Request failed";

  const body =
    status === 404
      ? "The link you followed may be broken, or the page may have been moved."
      : status === 401 || status === 403
        ? "Open this app from inside your Shopify admin to continue."
        : "We've logged the error and our team will look into it. Try again in a moment, or contact support if it keeps happening.";

  return (
    <main
      style={{
        fontFamily: "system-ui, sans-serif",
        margin: 0,
        padding: "4rem 1rem",
        background: "#fafafa",
        minHeight: "100vh",
      }}
    >
      <title>{`${status} — ${BRAND.name}`}</title>
      <div style={{ maxWidth: 640, margin: "0 auto", background: "white", padding: "2rem", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
        <p style={{ color: "#666", margin: 0, fontSize: "0.9em" }}>HTTP {status}</p>
        <h1 style={{ marginTop: "0.5rem" }}>{headline}</h1>
        <p>{body}</p>
        <p style={{ marginTop: "2rem" }}>
          <a href="/" style={{ color: BRAND.primaryColor }}>Go home</a>
          {" · "}
          <a href={`mailto:${BRAND.supportEmail}`} style={{ color: BRAND.primaryColor }}>Contact {BRAND.supportEmail}</a>
        </p>
        {!isProd && !isResponse ? (
          <pre style={{ marginTop: "2rem", padding: "1rem", background: "#f5f5f5", borderRadius: 4, fontSize: "0.85em", overflow: "auto" }}>
            {error instanceof Error ? `${error.stack ?? error.message}` : JSON.stringify(error, null, 2)}
          </pre>
        ) : null}
      </div>
    </main>
  );
}
